'use client';

import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { InfoPopover } from '@/components/ui/info-popover';
import { giftAidStartLabels } from '@/lib/giftaid/start-date-labels';
import type { SignupFormData } from './signup-wizard';

export function StepGiftAid({ form }: { form: UseFormReturn<SignupFormData> }) {
  // Derived per render so the options follow the 6 April boundary.
  const labels = giftAidStartLabels();

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Gift Aid Setup</h2>
        <p className="text-sm text-gray-500">Configure how your charity handles Gift Aid</p>
      </div>

      <FormField
        control={form.control}
        name="giftAidStartDate"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <div className="flex items-center gap-2">
              <FormLabel className="m-0">When did you start claiming Gift Aid?</FormLabel>
              <InfoPopover
                label="What is Gift Aid?"
                body="Gift Aid lets HMRC top up donations to your charity by 25% at no extra cost to the donor. To claim, you need to be registered with HMRC for Charities Online. The date you give us is the earliest donation we'll include in claims."
                helpAnchor="#what-is-gift-aid"
              />
            </div>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="current" id="current" />
                  <Label htmlFor="current" className="font-normal cursor-pointer">
                    {labels.current}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="last_year" id="last_year" />
                  <Label htmlFor="last_year" className="font-normal cursor-pointer">
                    {labels.last_year}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="earlier" id="earlier" />
                  <Label htmlFor="earlier" className="font-normal cursor-pointer">
                    {labels.earlier}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="not_claiming" id="not_claiming" />
                  <Label htmlFor="not_claiming" className="font-normal cursor-pointer">
                    Not claiming yet
                  </Label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="operatesCommunityBuildings"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <div className="flex items-center gap-2">
              <FormLabel className="m-0">Do you operate from community buildings?</FormLabel>
              <InfoPopover
                label="What counts as a community building?"
                body="If you collect cash at events held in places like church halls, scout huts, or village halls, GASDS gives you a higher annual cap — £8,000 per building instead of £8,000 total. The building must be open to the public and host at least 6 charitable events per year."
                helpAnchor="#community-buildings"
              />
            </div>
            <FormControl>
              <RadioGroup
                onValueChange={(value) => field.onChange(value === 'yes')}
                value={field.value ? 'yes' : 'no'}
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="buildings_yes" />
                  <Label htmlFor="buildings_yes" className="font-normal cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="buildings_no" />
                  <Label htmlFor="buildings_no" className="font-normal cursor-pointer">No</Label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormDescription>
              E.g., church halls, village halls, scout huts used for charitable activities
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
